<template>
  <div
    class="flex h-[500px] w-full flex-col justify-center px-10 dark:text-white"
  >
    <div class="mb-1 text-2xl font-bold">{{ $t("login.title") }}</div>
    <div class="mb-6 text-sm text-white/70">{{ $t("login.subtitle") }}</div>

    <div class="mb-4 flex gap-3">
      <el-select v-model="country" class="w-[120px]!" size="large">
        <el-option label="+86" value="+86" />
        <el-option label="+852" value="+852" />
        <el-option label="+853" value="+853" />
        <el-option label="+886" value="+886" />
        <el-option label="+1" value="+1" />
      </el-select>
      <el-input
        v-model="phone"
        :placeholder="$t('login.phonePlaceholder')"
        size="large"
      />
    </div>

    <div class="mb-4 flex gap-3" v-if="mode === 'code'">
      <el-input
        v-model="code"
        :placeholder="$t('login.codePlaceholder')"
        size="large"
      />
      <el-button
        :loading="codeLoading"
        type="success"
        plain
        size="large"
        @click="onSendCode"
        :disabled="!canSend"
      >
        {{ $t("login.sendCode") }}
      </el-button>
    </div>

    <div class="mb-4" v-else>
      <el-input
        v-model="password"
        :placeholder="$t('login.passwordPlaceholder')"
        type="password"
        size="large"
      />
    </div>
    <div class="mb-4">
      <el-link href="#" @click.prevent="toggleMode">
        {{
          mode === "password"
            ? $t("login.codeLogin")
            : $t("login.passwordLogin")
        }}
      </el-link>
    </div>

    <el-button
      type="primary"
      size="large"
      class="mb-4 w-full"
      :loading="submitLoading"
      :disabled="!canSubmit"
      @click="onSubmit"
    >
      {{ $t("login.submit") }}
    </el-button>

    <div class="flex items-center gap-2">
      <el-checkbox v-model="agree" />
      <div class="text-sm">
        {{ $t("login.agreePrefix") }}
        <a href="#" class="text-[#03DB6C]">{{ $t("login.userAgreement") }}</a>
        <span> / </span>
        <a href="#" class="text-[#03DB6C]">{{ $t("login.privacyPolicy") }}</a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
type Mode = "code" | "password";

const props = defineProps<{ mode?: Mode; submitLoading?: boolean }>();
const emit = defineEmits<{
  (
    e: "submit",
    payload: {
      country: string;
      phone: string;
      code?: string;
      password?: string;
    },
  ): void;
  (e: "send-code", payload: { country: string; phone: string }): void;
}>();

const country = ref<string>("+86");
const phone = ref<string>("");
const code = ref<string>("");
const password = ref<string>("");
const agree = ref<boolean>(false);
const codeLoading = ref<boolean>(false);
const submitting = ref<boolean>(false);
const tokenCookie = useCookie<string | null>("auth_token", {
  default: () => null,
});
const userState = useState("user", () => null as any);

const mode = ref<Mode>(props.mode ?? "code");
const submitLoading = computed<boolean>(
  () => props.submitLoading ?? submitting.value,
);

const canSend = computed(() => phone.value.trim().length >= 6);
const canSubmit = computed(() => {
  if (!agree.value) return false;
  if (mode.value === "code")
    return phone.value.trim().length >= 6 && code.value.trim().length >= 4;
  return phone.value.trim().length >= 6 && password.value.trim().length >= 6;
});

async function onSendCode() {
  if (!canSend.value || codeLoading.value) return;
  try {
    codeLoading.value = true;
    ElMessage.info("验证码服务未配置");
    emit("send-code", { country: country.value, phone: phone.value });
  } catch (e) {
    ElMessage.error("发送验证码失败");
  } finally {
    codeLoading.value = false;
  }
}

async function onSubmit() {
  if (!canSubmit.value || submitLoading.value) return;
  try {
    submitting.value = true;
    ElMessage.info("登录服务未配置");
    emit("submit", { country: country.value, phone: phone.value });
  } catch (e) {
  } finally {
    submitting.value = false;
  }
}

function toggleMode() {
  mode.value = mode.value === "password" ? "code" : "password";
}
</script>
